// Recover from stale dynamic-import chunks after a deployment.
//
// When a new build ships, the hosting service deletes the previous build's
// hashed JS chunks. A user who still has the OLD index.html (cached) then tries
// to lazy-load a chunk at a hash that no longer exists -> 404 ->
// "TypeError: Failed to fetch dynamically imported module" -> React Router
// throws during render -> the blank "error rendering this page" screen.
//
// Vite emits `vite:preloadError` for exactly this case. We also catch the same
// failure when it surfaces as a plain error/rejection (it can reach
// window.onerror / unhandledrejection instead of the Vite event, depending on
// how the import was triggered). In all cases we reload ONCE to pull the fresh
// index.html + chunks.
//
// Guard against a reload loop: if reloading doesn't fix it (offline, adblocker,
// genuinely missing asset), we must not reload forever. A sessionStorage flag
// caps it to one reload per tab session; it's cleared on a successful load.

const RELOAD_FLAG = "edgely_chunk_reload_attempted";
const CHUNK_ERR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

function isChunkLoadError(value: unknown): boolean {
  if (value instanceof Error) return CHUNK_ERR_RE.test(value.message);
  if (typeof value === "string") return CHUNK_ERR_RE.test(value);
  return false;
}

function reloadOnce(): void {
  if (typeof sessionStorage !== "undefined") {
    if (sessionStorage.getItem(RELOAD_FLAG)) return; // already tried this session
    try {
      sessionStorage.setItem(RELOAD_FLAG, "1");
    } catch {
      /* private mode / quota — fall through, still reload once best-effort */
    }
  }
  window.location.reload();
}

let installed = false;

export function installChunkReloadGuard(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // A normal load that reaches this point means we're on a working build; clear
  // the flag so a FUTURE deployment can trigger a fresh reload.
  if (typeof sessionStorage !== "undefined") {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      /* ignore */
    }
  }

  // Vite's first-class signal for a stale/missing chunk.
  window.addEventListener("vite:preloadError", (event: Event) => {
    (event as Event & { preventDefault: () => void }).preventDefault();
    reloadOnce();
  });

  // Same failure surfacing as a thrown error or a rejected dynamic import.
  window.addEventListener(
    "error",
    (event: ErrorEvent) => {
      if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
        reloadOnce();
      }
    },
    true,
  );

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      reloadOnce();
    }
  });
}

// Exported for unit testing the matcher.
export { isChunkLoadError };
